import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PersonasListSection } from '../PersonasListSection';
import { GarantesListSection } from './GarantesListSection';
import otrosTramitesService from '../../services/otrosTramitesService';
import type { OtroTramiteRecord } from '../../types';

interface PersonasListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'otorgantes' | 'responsables' | 'garantes';
  tramite: OtroTramiteRecord;
}

export const PersonasListDialog: React.FC<PersonasListDialogProps> = ({
  open,
  onOpenChange,
  type,
  tramite,
}) => {
  const isGarantes = type === 'garantes';

  const personas = type === 'otorgantes' ? tramite.otorgantes : tramite.responsables;
  const resolvedDniDocuments = tramite.dniDocuments ?? undefined;

  const title = type === 'otorgantes'
    ? `Otorgantes del trámite ${tramite.id}`
    : type === 'responsables'
      ? `Responsables del trámite ${tramite.id}`
      : `Garantes del trámite ${tramite.id}`;

  const sectionTitle = type === 'otorgantes'
    ? 'Otorgantes asociados'
    : type === 'responsables'
      ? 'Responsables asociados'
      : 'Garantes asociados';

  const emptyMessage = type === 'otorgantes'
    ? 'No hay otorgantes registrados para este trámite.'
    : type === 'responsables'
      ? 'No hay responsables registrados para este trámite.'
      : 'No hay garantes registrados para este trámite.';

  const dniDocumentsQuery = useQuery({
    queryKey: ['otros-tramites', 'personas-list-dni', tramite.id, type],
    queryFn: () =>
      otrosTramitesService.getDniDocumentsByPersonas({
        otorganteIds: type === 'otorgantes' ? personas.map((persona) => persona.id) : [],
        responsableIds: type === 'responsables' ? personas.map((persona) => persona.id) : [],
      }),
    // Garantes no usan este query — su DNI viene directo en el record
    enabled: !isGarantes && open && Boolean(personas.length) && !resolvedDniDocuments,
    staleTime: 1000 * 30,
  });

  const descriptionText = type === 'otorgantes'
    ? 'Información de contacto de los otorgantes asociados.'
    : type === 'responsables'
      ? 'Información de contacto de los responsables asociados.'
      : 'Garantes vinculados a este trámite con sus documentos adjuntos.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{descriptionText}</DialogDescription>
        </DialogHeader>

        {isGarantes ? (
          <GarantesListSection garantes={tramite.garantes ?? []} />
        ) : personas.length > 0 ? (
          <PersonasListSection
            title={sectionTitle}
            personas={personas}
            personaType={type === 'otorgantes' ? 'otorgante' : 'responsable'}
            dniDocuments={resolvedDniDocuments ?? dniDocumentsQuery.data}
            isLoadingDniDocuments={dniDocumentsQuery.isLoading || dniDocumentsQuery.isFetching}
            dniDocumentsError={dniDocumentsQuery.isError}
          />
        ) : (
          <p className="text-center text-sm text-gray-500 py-8">{emptyMessage}</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
